import { clsx } from 'clsx';
import type { KeyboardEventHandler, MouseEvent, ReactNode } from 'react';

import { getAccessibilityProps } from '../../internal/utils/getAccessibilityProps';
import type { AccessibilityProps } from '../../internal/utils/getAccessibilityProps';
import type { ColorKey } from '../../theme/themeColors.types';
import type { ReeferBaseProps } from '../../utils/baseProps.types';
import { getColorCSSVar } from '../../utils/colors';
import { toCSSSize } from '../../utils/css';
import { fakeButtonProps, handleEnterKey } from '../../utils/handleEnterKey';
import { marginCSSVars } from '../../utils/spacing';
import type { MarginProperties } from '../../utils/spacing.types';
import styles from './card.module.css';
import { CardAction } from './cardAction/cardAction';
import { CardContent } from './cardContent/cardContent';
import { CardGroup } from './cardGroup/cardGroup';
import { CardImage } from './cardImage/cardImage';

interface CardBaseProps {
  /** Applies a border around the entire `Card`component in a variety of predefined styles */
  border?: ColorKey | 'none';

  /** Optionally change the border width, defaults to 1px. For `Card.Content` can use shorthand to set bottom, top, left and right border widths eg. `0 0 1px 0`. */
  borderWidth?: string;

  /** Child elements of the `Card` component */
  children: ReactNode;

  /** Set a fixed height for the `Card` component, accepts any valid `height` value */
  height?: string | number;

  /** Sets `onClick` handler for the `Card` component itself (not the `Card` action) */
  onClick?: ((e: MouseEvent<HTMLElement>) => void) | (() => void);

  /** Set a fixed width for the card, accepts any valid `width` value */
  width?: string | number;
}

interface CardProps
  extends AccessibilityProps, CardBaseProps, ReeferBaseProps, MarginProperties {
  /** Disables the box shadow */
  flat?: boolean;

  /** Part names allows CSS to select and style specific elements in a shadow tree via the ::part pseudo-element. */
  part?: string;
}

/**
 * The `Card` component is a wrapper component used for displaying a styled card with an optional `border` and `box-shadow`.
 * `Card` contents can be included via sub-components `Card.Action`, `Card.Content` and `Card.Image`.
 */

function BaseCard({
  ariaLabel,
  ariaLabelledBy,
  border = 'none',
  borderWidth = '1px',
  children,
  className,
  'data-testid': testId,
  flat = false,
  height = 'auto',
  id,
  onClick,
  part,
  role,
  style,
  width = 'auto',
  ...marginProps
}: CardProps) {
  const a11yProps = getAccessibilityProps({
    ariaLabel,
    ariaLabelledBy,
    role,
    onClick,
  });

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={clsx(className, styles['card'], {
        [styles['card__clickable']]: !!onClick,
        [styles['card__flat']]: flat,
      })}
      id={id}
      style={{
        '--card-border':
          border === 'none'
            ? 'none'
            : `${borderWidth} solid ${getColorCSSVar(border)}`,
        '--card-height': toCSSSize(height),
        ...marginCSSVars(marginProps),
        '--card-width': toCSSSize(width),
        ...style,
      }}
      data-testid={testId}
      onClick={onClick}
      onKeyUp={(event) =>
        handleEnterKey(event, onClick as unknown as KeyboardEventHandler)
      }
      {...{ part }}
      {...(onClick && fakeButtonProps)}
      {...a11yProps}
    >
      {children}
    </div>
  );
}

export type { CardBaseProps, CardProps };

export const Card = Object.assign(BaseCard, {
  Image: CardImage,
  Action: CardAction,
  Content: CardContent,
  Group: CardGroup,
});
